import { useCallback, useRef, useEffect } from 'react';
import { useBenchmarkUploadMutation } from '@/__generated__/graphql';
import { UploadBenchmarkProps } from './types';
import { UPLOAD_PARALLEL_REQUESTS, UPLOAD_NUMBER_REQUESTS, UPLOAD_REQUEST_SIZE } from './constants';

const makePseudoRandomString = (length: number): string => {
  let data = '';

  let current = Math.floor(Math.random() * (1 << 52));
  while (data.length < length) {
    const a = (current >> 0) & 0xff;

    const b = (current >> 8) & 0xff;

    const c = (current >> 16) & 0xff;

    const d = (current >> 24) & 0xff;

    current = (current * 103) & 0xffff_ffff;
    data += String.fromCodePoint(a, b, c, d);
  }
  return data;
};

export const UploadBenchmark = ({ onProgress, onComplete }: UploadBenchmarkProps) => {
  const [benchmarkUploadMutation] = useBenchmarkUploadMutation();

  const shared = useRef({ completeCount: 0, numberErrors: 0, totalBytes: 0, startTime: 0 });

  const runUploadBenchmarkBatch = useCallback(
    async (batchSize: number, expectedCompleteCount: number) => {
      const data = makePseudoRandomString(UPLOAD_REQUEST_SIZE);
      for (let requestCount = 0; requestCount < batchSize; requestCount += 1) {
        const result = await benchmarkUploadMutation({
          variables: {
            input: {
              data,
            },
          },
        });
        if (result.data?.benchmarkUpload?.size === data.length) {
          shared.current.totalBytes += data.length;
        } else {
          // error
          shared.current.numberErrors += 1;
        }
        shared.current.completeCount += 1;
        if (shared.current.completeCount < expectedCompleteCount) {
          onProgress(Math.round((shared.current.completeCount / expectedCompleteCount) * 100));
        } else {
          onProgress(100);
          const { startTime, totalBytes, numberErrors } = shared.current;
          const timeElapsed = (performance.now() - startTime) * 0.001;
          const speedMbitPerSec = Math.round((totalBytes * (8 * 0.000001)) / timeElapsed);

          onComplete({
            speedMbitPerSec,
            bytesTransfered: totalBytes,
            numberErrors,
            timeElapsed,
          });
        }
      }
    },
    [shared, benchmarkUploadMutation, onProgress, onComplete]
  );

  const runAll = useCallback(async () => {
    shared.current.startTime = performance.now();
    const batchSize = Math.floor(UPLOAD_NUMBER_REQUESTS / UPLOAD_PARALLEL_REQUESTS);
    await Promise.all(
      [...Array(UPLOAD_PARALLEL_REQUESTS)].map(() =>
        runUploadBenchmarkBatch(batchSize, batchSize * UPLOAD_PARALLEL_REQUESTS)
      )
    );
  }, [shared, runUploadBenchmarkBatch]);

  useEffect(() => {
    runAll();
  }, [runAll]);

  return <></>;
};
